/**
 * Global fetch interceptor that appends `lang` query parameter to all requests.
 *
 * The Akamai CDN at comptes.pprod.bncd.ca / accounts.pprod.nbdb.ca issues a
 * 301 Moved Permanently redirect to append ?lang=<locale>. Since 301 converts
 * POST → GET per HTTP spec, all API calls break.
 *
 * By pre-appending the lang parameter on ALL outgoing fetch requests (including
 * those made by third-party libraries like bnc-react-forms), the CDN skips the
 * redirect and forwards the request as-is.
 */
import { loadLocale } from 'utils';

const originalFetch = window.fetch;

const appendLangToUrl = (url: string): string => {
  const locale = loadLocale() || 'fr';
  // Only append if not already present
  if (url.includes('lang=')) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${locale}`;
};

window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof input === 'string') {
    return originalFetch.call(this, appendLangToUrl(input), init);
  }

  if (input instanceof Request) {
    const modifiedUrl = appendLangToUrl(input.url);
    const modifiedRequest = new Request(modifiedUrl, input);
    return originalFetch.call(this, modifiedRequest, init);
  }

  // URL object
  const urlStr = input.toString();
  return originalFetch.call(this, appendLangToUrl(urlStr), init);
};
